import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { 
  Search, 
  FileText, 
  Users, 
  DollarSign, 
  MapPin, 
  Calendar, 
  MessageSquare,
  Eye,
  EyeOff,
  Heart,
  HeartOff,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp
} from 'lucide-react'

interface EnterpriseRfq {
  id: string
  title: string
  description: string
  deadline: string
  budgetRange?: string
  location?: string
  buyerCompany?: string
  buyerLocation?: string
  responseCount?: number
  createdAt: string
  attachmentCount?: number
  isSeen?: boolean
  isLiked?: boolean
  hasResponded?: boolean
  responseStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED'
  responseDate?: string
}

// Response Count Badge Component
const ResponseCountBadge: React.FC<{ count: number }> = ({ count }) => {
  const isHot = count > 10
  const isFirst = count === 0
  
  if (isFirst) {
    return (
      <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <MessageSquare className="h-3 w-3 mr-1" />
        Be the first to respond!
      </div>
    )
  }
  
  if (isHot) {
    return (
      <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
        <TrendingUp className="h-3 w-3 mr-1" />
        {count} vendors responded
      </div>
    )
  }
  
  return (
    <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
      <Users className="h-3 w-3 mr-1" />
      {count} vendor{count === 1 ? '' : 's'} responded
    </div>
  )
}

// Time Left Component
const TimeLeftBadge: React.FC<{ deadline: string }> = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState<string>('')
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date()
      const deadlineDate = new Date(deadline)
      const diff = deadlineDate.getTime() - now.getTime()
      
      if (diff <= 0) {
        setTimeLeft('Expired')
        return
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      
      if (days > 0) {
        setTimeLeft(`${days} day${days === 1 ? '' : 's'} left`)
      } else if (hours > 0) {
        setTimeLeft(`${hours} hour${hours === 1 ? '' : 's'} left`)
      } else {
        setTimeLeft('Less than 1 hour left')
      }
    }
    
    calculateTimeLeft()
    const interval = setInterval(calculateTimeLeft, 60000) // Update every minute
    
    return () => clearInterval(interval)
  }, [deadline])
  
  if (timeLeft === 'Expired') {
    return (
      <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
        <Clock className="h-3 w-3 mr-1" />
        Expired
      </div>
    )
  }
  
  const isUrgent = timeLeft.includes('hour') || timeLeft.includes('1 day')
  
  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
      isUrgent 
        ? 'bg-orange-50 text-orange-700 border-orange-200' 
        : 'bg-gray-50 text-gray-700 border-gray-200'
    }`}>
      <Clock className="h-3 w-3 mr-1" />
      {timeLeft}
    </div>
  )
}

export const VendorResponsesPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'responded' | 'liked' | 'seen'>('responded')
  const [rfqs, setRfqs] = useState<EnterpriseRfq[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }
    const isEnterpriseVendor = (user.role === 'VENDOR' && user.isEnterprise) || user.role === 'ENTERPRISE_VENDOR'
    if (!isEnterpriseVendor) {
      navigate('/', { replace: true })
      return
    }
  }, [user, navigate])

  useEffect(() => {
    fetchRfqs()
  }, [activeTab])

  const fetchRfqs = async () => {
    try {
      setLoading(true)
      setError(null)
      
      let endpoint = ''
      switch (activeTab) {
        case 'responded':
          endpoint = '/api/enterprise/rfqs/vendor-responses'
          break
        case 'liked':
          endpoint = '/api/enterprise/rfqs/vendor-liked'
          break
        case 'seen':
          endpoint = '/api/enterprise/rfqs/vendor-seen'
          break
      }
      
      const response = await apiService.get<any>(endpoint) as any
      if (response?.success) {
        setRfqs(response.data?.rfqs || [])
      } else {
        setError('Failed to fetch RFQs')
      }
    } catch (err) {
      console.error('Error fetching RFQs:', err)
      setError('Failed to fetch RFQs')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleMarkAsSeen = async (rfqId: string, isSeen: boolean) => {
    try {
      const response = await apiService.patch(`/api/enterprise/rfqs/${rfqId}/mark-seen`, {
        isSeen
      })
      
      if ((response as any)?.success) {
        // Update local state
        setRfqs(prev => prev.map(rfq => 
          rfq.id === rfqId ? { ...rfq, isSeen } : rfq
        ))
      }
    } catch (error) {
      console.error('Error marking RFQ as seen:', error)
    }
  }

  const handleToggleLike = async (rfqId: string, isLiked: boolean) => {
    try {
      const response = await apiService.patch(`/api/enterprise/rfqs/${rfqId}/toggle-like`, {
        isLiked
      })
      if ((response as any)?.success) {
        // Update local state
        setRfqs(prev => prev.map(rfq => 
          rfq.id === rfqId ? { ...rfq, isLiked } : rfq
        ))
      }
    } catch (error) {
      console.error('Error toggling RFQ like:', error)
    }
  }

  const handleRespondToRfq = (rfq: EnterpriseRfq) => {
    navigate(`/enterprise/vendor-rfqs?respond=${rfq.id}`)
  }

  const filteredRfqs = rfqs.filter(rfq =>
    rfq.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rfq.buyerCompany?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rfq.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getStatusBadge = (rfq: EnterpriseRfq) => {
    if (rfq.responseStatus === 'ACCEPTED') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Accepted
        </span>
      )
    }
    if (rfq.responseStatus === 'REJECTED') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          Rejected
        </span>
      )
    }
    if (rfq.responseStatus === 'PENDING') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>
      )
    }
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My RFQ Responses</h1>
              <p className="mt-2 text-gray-600">
                Track your RFQ interactions, responses, and saved opportunities
              </p>
            </div>
            <button
              onClick={() => navigate('/enterprise/vendor-rfqs')}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <FileText className="h-4 w-4 mr-2" />
              Browse New RFQs
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('responded')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'responded'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Responded ({rfqs.filter(r => r.hasResponded).length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab('liked')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'liked'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Heart className="h-4 w-4 mr-2" />
                  Liked ({rfqs.filter(r => r.isLiked).length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab('seen')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'seen'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Eye className="h-4 w-4 mr-2" />
                  Seen ({rfqs.filter(r => r.isSeen && !r.isLiked && !r.hasResponded).length})
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search RFQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading RFQs...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">{error}</div>
              <button
                onClick={fetchRfqs}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            </div>
          ) : filteredRfqs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {activeTab === 'responded' ? 'No Responses Yet' : 
                 activeTab === 'liked' ? 'No Liked RFQs' : 'No Seen RFQs'}
              </h3>
              <p className="text-gray-600 mb-6">
                {activeTab === 'responded' ? 'Start responding to RFQs to see them here.' :
                 activeTab === 'liked' ? 'Like RFQs to save them for later review.' :
                 'Mark RFQs as seen to track your browsing history.'}
              </p>
              <button
                onClick={() => navigate('/enterprise/vendor-rfqs')}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg"
              >
                Browse RFQs
              </button>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredRfqs.map((rfq) => (
                  <div key={rfq.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                        {rfq.title}
                      </h3>
                      <div className="flex flex-col items-end space-y-2">
                        {getStatusBadge(rfq)}
                        <TimeLeftBadge deadline={rfq.deadline} />
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {rfq.description}
                    </p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="h-4 w-4 mr-2" />
                        <span>{rfq.buyerCompany || 'Unknown Company'}</span>
                      </div>
                      {rfq.budgetRange && (
                        <div className="flex items-center text-sm text-gray-500">
                          <DollarSign className="h-4 w-4 mr-2" />
                          <span>{rfq.budgetRange}</span>
                        </div>
                      )}
                      {rfq.location && (
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="h-4 w-4 mr-2" />
                          <span>{rfq.location}</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>Deadline: {formatDate(rfq.deadline)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex flex-col space-y-2">
                        <span className="text-xs text-gray-400">
                          Posted {formatDate(rfq.createdAt)}
                        </span>
                        <ResponseCountBadge count={rfq.responseCount || 0} />
                      </div>
                      <div className="flex items-center space-x-2">
                        {/* Seen/Unseen Toggle */}
                        <button
                          onClick={() => handleMarkAsSeen(rfq.id, !rfq.isSeen)}
                          className={`p-2 rounded-full transition-colors ${
                            rfq.isSeen 
                              ? 'text-blue-600 hover:text-blue-700' 
                              : 'text-gray-400 hover:text-gray-600'
                          }`}
                          title={rfq.isSeen ? 'Mark as unseen' : 'Mark as seen'}
                        >
                          {rfq.isSeen ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        
                        {/* Like/Unlike Toggle */}
                        <button
                          onClick={() => handleToggleLike(rfq.id, !rfq.isLiked)}
                          className={`p-2 rounded-full transition-colors ${
                            rfq.isLiked 
                              ? 'text-red-600 hover:text-red-700' 
                              : 'text-gray-400 hover:text-gray-600'
                          }`}
                          title={rfq.isLiked ? 'Unlike' : 'Like'}
                        >
                          {rfq.isLiked ? <Heart className="h-4 w-4" /> : <HeartOff className="h-4 w-4" />}
                        </button>
                        
                        {/* Respond Button */}
                        {!rfq.hasResponded && (
                          <button
                            onClick={() => handleRespondToRfq(rfq)}
                            className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Respond
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 